// @ts-nocheck
import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSettings } from './SettingsContext';

export default function SettingsDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { settings, setSettings, reset, importSettings, exportSettings } = useSettings();

  const update = (patch: Partial<typeof settings>) => setSettings({ ...settings, ...patch });

  const json = useMemo(() => exportSettings(), [exportSettings, settings]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-black/40" onClick={onClose} />
          <motion.div
            className="absolute right-0 top-0 h-full w-full max-w-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-xl p-6 overflow-y-auto"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.25 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Ajustes</h2>
              <button onClick={onClose} className="px-3 py-1 rounded bg-gray-100 dark:bg-gray-800">Cerrar</button>
            </div>

            {/* Display & theme */}
            <section className="space-y-3 mb-6">
              <h3 className="font-semibold">Display & theme</h3>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-sm">Tema</label>
                <select value={settings.theme} onChange={(e)=>update({ theme: e.target.value as any })} className="w-full border rounded px-2 py-1">
                  <option value="system">Sistema</option>
                  <option value="light">Claro</option>
                  <option value="dark">Oscuro</option>
                  <option value="contrast">Alto contraste</option>
                </select>
                <label className="text-sm">Densidad</label>
                <select value={settings.density} onChange={(e)=>update({ density: e.target.value as any })} className="w-full border rounded px-2 py-1">
                  <option value="comfortable">Cómoda</option>
                  <option value="compact">Compacta</option>
                </select>
                <label className="text-sm">Layout</label>
                <select value={settings.layout} onChange={(e)=>update({ layout: e.target.value as any })} className="w-full border rounded px-2 py-1">
                  <option value="grid">Grid</option>
                  <option value="list">Lista</option>
                  <option value="map">Mapa primero</option>
                </select>
                <label className="text-sm">Reducir animaciones</label>
                <input type="checkbox" checked={settings.reduce_motion} onChange={(e)=>update({ reduce_motion: e.target.checked })} />
              </div>
            </section>

            {/* Content & relevance */}
            <section className="space-y-3 mb-6">
              <h3 className="font-semibold">Contenido & relevancia</h3>
              <div className="grid grid-cols-2 gap-2 items-center">
                <label className="text-sm">Orden por defecto</label>
                <select value={settings.default_sort} onChange={(e)=>update({ default_sort: e.target.value as any })} className="w-full border rounded px-2 py-1">
                  <option value="distance">Distancia</option>
                  <option value="rating">Rating</option>
                  <option value="recent">Reciente</option>
                  <option value="popular">Popularidad</option>
                </select>
                <label className="text-sm">Radio (km)</label>
                <input type="range" min={5} max={50} value={settings.radius_km} onChange={(e)=>update({ radius_km: parseInt(e.target.value,10) })} />
                <div className="col-span-2 text-xs text-gray-500">{settings.radius_km} km</div>
                <label className="text-sm">Plataformas</label>
                <div className="flex gap-2 flex-wrap">
                  {['TikTok','Instagram','YouTube','Facebook'].map(p => (
                    <label key={p} className="text-sm inline-flex items-center gap-1 border rounded px-2 py-1">
                      <input type="checkbox" checked={settings.platforms.includes(p as any)} onChange={(e)=>{
                        const next = e.target.checked ? [...settings.platforms, p as any] : settings.platforms.filter(x=>x!==p);
                        update({ platforms: next as any });
                      }} /> {p}
                    </label>
                  ))}
                </div>
                <label className="text-sm">Cocinas prioritarias</label>
                <input
                  placeholder="Ej. japonesa, italiana"
                  className="w-full border rounded px-2 py-1"
                  value={settings.cuisines.join(', ')}
                  onChange={(e)=>update({ cuisines: e.target.value.split(',').map(s=>s.trim()).filter(Boolean) })}
                />
                <label className="text-sm">Filtros dietarios</label>
                <input
                  placeholder="Ej. vegano, vegetariano, sin gluten"
                  className="w-full border rounded px-2 py-1"
                  value={settings.dietary_filters?.join(', ') || ''}
                  onChange={(e)=>update({ dietary_filters: e.target.value.split(',').map(s=>s.trim()).filter(Boolean) })}
                />
                <label className="text-sm">Idioma</label>
                <select value={settings.language} onChange={(e)=>update({ language: e.target.value as any })} className="w-full border rounded px-2 py-1">
                  <option value="es">Español</option>
                  <option value="en">English</option>
                </select>
              </div>
            </section>

            {/* Map & navigation */}
            <section className="space-y-3 mb-6">
              <h3 className="font-semibold">Mapa & navegación</h3>
              <div className="grid grid-cols-2 gap-2 items-center">
                <label className="text-sm">Mapa por defecto</label>
                <select value={settings.map_style} onChange={(e)=>update({ map_style: e.target.value as any })} className="w-full border rounded px-2 py-1">
                  <option value="standard">Estándar</option>
                  <option value="satellite">Satélite</option>
                </select>
                <label className="text-sm">Direcciones</label>
                <select value={settings.directions_pref} onChange={(e)=>update({ directions_pref: e.target.value as any })} className="w-full border rounded px-2 py-1">
                  <option value="google">Google</option>
                  <option value="apple">Apple</option>
                  <option value="waze">Waze</option>
                  <option value="uber">Uber</option>
                </select>
              </div>
            </section>

            {/* Privacy & sharing */}
            <section className="space-y-3 mb-6">
              <h3 className="font-semibold">Privacidad & compartir</h3>
              <div className="grid grid-cols-2 gap-2 items-center">
                <label className="text-sm">Visibilidad por defecto</label>
                <select value={settings.default_visibility} onChange={(e)=>update({ default_visibility: e.target.value as any })} className="w-full border rounded px-2 py-1">
                  <option value="private">Privado</option>
                  <option value="unlisted">No listado</option>
                  <option value="public">Público</option>
                </select>
                <label className="text-sm">Permitir comentarios</label>
                <input type="checkbox" checked={!!settings.share_options.allow_comments} onChange={(e)=>update({ share_options: { ...settings.share_options, allow_comments: e.target.checked } })} />
                <label className="text-sm">Permitir colaboradores</label>
                <input type="checkbox" checked={!!settings.share_options.allow_collaborators} onChange={(e)=>update({ share_options: { ...settings.share_options, allow_collaborators: e.target.checked } })} />
                <label className="text-sm">Mostrar creador</label>
                <input type="checkbox" checked={!!settings.share_options.show_creator} onChange={(e)=>update({ share_options: { ...settings.share_options, show_creator: e.target.checked } })} />
              </div>
            </section>

            {/* Data management */}
            <section className="space-y-3 mb-6">
              <h3 className="font-semibold">Gestión de datos</h3>
              <div className="flex gap-2">
                <button className="px-3 py-1 rounded bg-gray-100 dark:bg-gray-800" onClick={()=>reset()}>Restablecer</button>
                <button className="px-3 py-1 rounded bg-gray-100 dark:bg-gray-800" onClick={()=>{
                  const blob = new Blob([json], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = 'foodspotter-settings.json'; a.click();
                  URL.revokeObjectURL(url);
                }}>Exportar JSON</button>
                <label className="px-3 py-1 rounded bg-gray-100 dark:bg-gray-800 cursor-pointer">
                  Importar JSON
                  <input type="file" className="hidden" accept="application/json" onChange={async (e)=>{
                    const f = e.target.files?.[0]; if (!f) return;
                    const text = await f.text();
                    importSettings(text);
                  }} />
                </label>
                <button className="px-3 py-1 rounded bg-gray-100 dark:bg-gray-800" onClick={()=>{ localStorage.clear(); location.reload(); }}>Borrar caché local</button>
              </div>
            </section>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
